import {
  AUTH_EMAIL_MAX_LENGTH,
  AUTH_LOGIN_PASSWORD_MAX_LENGTH,
  hasControlCharacters,
  sanitizeEmailInput,
} from "@playbrain/auth/dashboard"
import validator from "validator"
import * as z from "zod"
import {
  dashboardPermissionActionsForResource,
  dashboardPermissionFeatures,
} from "../../components/users/dashboard-role-permissions"
import type { useDashboardMessage } from "../i18n/dashboard-messages"
import { isDashboardUserAccessFeature } from "./user-access-grants"

export const dashboardRoleKeySchema = z
  .string()
  .trim()
  .regex(/^[a-z][a-z0-9-]{1,62}$/, "Use lowercase letters, numbers, and hyphens.")
  .refine((value) => value !== "none" && value !== "superadmin", "Select a role.")

export const DASHBOARD_USERNAME_MAX_LENGTH = 64

type DashboardT = ReturnType<typeof useDashboardMessage>
type DashboardUserValidationMessages = {
  emailInvalid: string
  emailRequired: string
  emailTooLong: string
  invalidValue: string
  passwordRequired: string
  passwordStrength: string
  passwordTooLong: string
  passwordUnsupported: string
  usernameRequired: string
  usernameTooLong: string
  usernameUnsupported: string
}

export const dashboardUsernameSchema = z
  .string()
  .min(1, "Username is required.")
  .max(
    DASHBOARD_USERNAME_MAX_LENGTH,
    `Username must be ${DASHBOARD_USERNAME_MAX_LENGTH} characters or fewer.`
  )
  .refine((value) => !hasControlCharacters(value), "Username contains unsupported characters.")

export const dashboardPasswordSchema = z
  .string()
  .min(1, "Password is required.")
  .max(AUTH_LOGIN_PASSWORD_MAX_LENGTH, "Password is too long.")
  .refine((value) => !hasControlCharacters(value), "Password contains unsupported characters.")
  .refine(isStrongDashboardPassword, "Use at least 8 characters with uppercase, lowercase, and a number.")

export const dashboardEmailSchema = z
  .string()
  .transform((value) => sanitizeEmailInput(value))
  .pipe(
    z
      .string()
      .min(1, "Email is required.")
      .max(AUTH_EMAIL_MAX_LENGTH, "Email is too long.")
      .email("Enter a valid email.")
  )

export const dashboardRoleSchema = dashboardRoleKeySchema
export const dashboardLanguageSchema = z.enum(["en", "ja"])

const assignableDashboardPermissionResources = new Set(
  dashboardPermissionFeatures.flatMap((feature) => feature.resources)
)
type AssignableDashboardPermissionResource = (typeof dashboardPermissionFeatures)[number]["resources"][number]

export const dashboardPermissionKeySchema = z.string().refine((value) => {
  const [resource, action, ...rest] = value.split(":")
  return (
    rest.length === 0 &&
    assignableDashboardPermissionResources.has(resource as AssignableDashboardPermissionResource) &&
    dashboardPermissionActionsForResource(resource as AssignableDashboardPermissionResource).includes(
      action as ReturnType<typeof dashboardPermissionActionsForResource>[number]
    )
  )
}, "Invalid permission.")

const dashboardAccessFeatureSchema = z.string().refine(isDashboardUserAccessFeature, "Invalid feature.")
/**
 * A grant's access is either one of the three legacy levels or the key of a role
 * from the Roles page, so it cannot be checked against a fixed list — only that
 * it is a non-empty string. Mirrors `normalizeAccessGrants` on the server, which
 * has accepted role keys since roles became user-defined; this schema did not,
 * so assigning any real role was rejected as an invalid value.
 */
const dashboardAccessLevelSchema = z.string().trim().min(1, "Select an access level.")
const dashboardAccessGrantsSchema = z
  .array(
    z.object({
      feature: dashboardAccessFeatureSchema,
      access: dashboardAccessLevelSchema,
    })
  )
  .default([])

export const createDashboardUserSchema = z.object({
  username: dashboardUsernameSchema,
  password: dashboardPasswordSchema,
  email: dashboardEmailSchema,
  accessFeature: dashboardAccessFeatureSchema.optional(),
  accessLevel: dashboardAccessLevelSchema.optional(),
  accessGrants: dashboardAccessGrantsSchema,
})

export const updateDashboardUserSchema = z.object({
  username: dashboardUsernameSchema,
  accessFeature: dashboardAccessFeatureSchema.optional(),
  accessLevel: dashboardAccessLevelSchema.optional(),
  accessGrants: dashboardAccessGrantsSchema,
  dashboardLanguage: dashboardLanguageSchema,
})

export const createDashboardRoleSchema = z
  .object({
    name: z.string().trim().min(1, "Role name is required."),
    description: z.string().trim().optional(),
    permissions: z.array(dashboardPermissionKeySchema),
  })
  .strict()

/**
 * Every field but the id is optional: the disable toggle submits only
 * `{ id, disabled }`, and requiring a name here rejected that payload as
 * "Role name is required" — which is why disabling a role appeared to do
 * nothing. The edit dialog still sends the full shape.
 */
export const updateDashboardRoleSchema = z.object({
  id: z.string().min(1),
  name: z.string().trim().min(1, "Role name is required.").optional(),
  description: z.string().trim().optional(),
  permissions: z.array(dashboardPermissionKeySchema).optional(),
  disabled: z.boolean().optional(),
})

export function isStrongDashboardPassword(value: string) {
  return validator.isStrongPassword(value, {
    minLength: 8,
    minLowercase: 1,
    minUppercase: 1,
    minNumbers: 1,
    minSymbols: 0,
  })
}

export function zodFieldError(schema: z.ZodType, value: unknown) {
  const parsed = schema.safeParse(value)
  return parsed.success ? true : (parsed.error.issues[0]?.message ?? "Invalid value.")
}

export function dashboardUserValidationMessages(t: DashboardT): DashboardUserValidationMessages {
  return {
    emailInvalid: t("dashboard_users_email_invalid"),
    emailRequired: t("dashboard_users_email_required"),
    emailTooLong: t("dashboard_users_email_too_long"),
    invalidValue: t("dashboard_users_invalid_value"),
    passwordRequired: t("dashboard_users_password_required"),
    passwordStrength: t("dashboard_users_password_strength"),
    passwordTooLong: t("dashboard_users_password_too_long"),
    passwordUnsupported: t("dashboard_users_password_unsupported"),
    usernameRequired: t("dashboard_users_username_required"),
    usernameTooLong: t("dashboard_users_username_too_long", { max: DASHBOARD_USERNAME_MAX_LENGTH }),
    usernameUnsupported: t("dashboard_users_username_unsupported"),
  }
}

export function dashboardUsernameFieldError(value: string, messages: DashboardUserValidationMessages) {
  if (!value) return messages.usernameRequired
  if (value.length > DASHBOARD_USERNAME_MAX_LENGTH) return messages.usernameTooLong
  if (hasControlCharacters(value)) return messages.usernameUnsupported
  return true
}

export function dashboardPasswordFieldError(value: string, messages: DashboardUserValidationMessages) {
  if (!value) return messages.passwordRequired
  if (value.length > AUTH_LOGIN_PASSWORD_MAX_LENGTH) return messages.passwordTooLong
  if (hasControlCharacters(value)) return messages.passwordUnsupported
  if (!isStrongDashboardPassword(value)) return messages.passwordStrength
  return true
}

export function dashboardEmailFieldError(value: string, messages: DashboardUserValidationMessages) {
  const email = sanitizeEmailInput(value)
  if (!email) return messages.emailRequired
  if (email.length > AUTH_EMAIL_MAX_LENGTH) return messages.emailTooLong
  if (!validator.isEmail(email)) return messages.emailInvalid
  return true
}
